import { IconProp, SizeProp } from '@fortawesome/fontawesome-svg-core';
import { faEye } from '@fortawesome/free-solid-svg-icons/faEye';
import { faEyeSlash } from '@fortawesome/free-solid-svg-icons/faEyeSlash';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons/faInfoCircle';
import { Tooltip } from 'antd';
import { t } from 'i18next';
import merge from 'lodash-es/merge';
import React, { InputHTMLAttributes, useState } from 'react';
import styled from 'styled-components';

import { useTheme } from '../ThemeContext';
import i18n, { AppLangs } from '../i18n';
import { Rounded } from '../theme/Rounded';
import { Spacing } from '../theme/Spacing';
import { ThemeType } from '../theme/Theme';
import { AtiraIcon } from './AtiraIcon';
import { Button } from './Button';
import { Flex } from './Flex';
import { Text } from './Text';
import {
  LabelDirection,
  TitleAlignment,
} from './shared/types/SharedLabelProps';

const ShowHidePasswordButton = styled(Button)<{ hasError?: boolean }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: ${i18n.language === AppLangs.AR ? 'auto' : Spacing.s};
  left: ${i18n.language === AppLangs.AR ? Spacing.s : 'auto'};
  margin: 0;
  padding: 0;
  background: none;
  border: none;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.theme.main};
`;

interface StyledInputProps {
  width?: string;
  height?: string;
  border?: string;
  borderRadius?: keyof typeof Rounded;
  padding?: string;
  margin?: string;
  color?: string;
  originalType?: string;
  'data-testid'?: string;
}

// @ts-ignore
const StyledInput = styled.input.attrs((props) => ({
  // @ts-ignore
  'data-testid': props['data-testid'],
}))<Omit<StyledInputProps, 'dataTestId'>>`
  width: ${({ width }) => width || '100%'};
  height: ${({ height }) => height || '2.3rem'};
  margin: ${({ margin }) => margin || '0'};
  border: ${({ border }) => border || '0'};
  border-radius: ${({ borderRadius }) => borderRadius || Rounded.md};
  color: ${({ theme }) => theme.fgInput};
  transition: box-shadow 0.2s;
  outline: none !important;
  border-radius: ${Rounded.md};
  font-family: inherit;
  background-color: ${({ theme }) => theme.bgInput};

  html[dir='rtl'] & {
    &::placeholder {
      text-align: right;
    }
  }

  ${({ originalType }) =>
    originalType === 'password'
      ? `
      padding-inline-end: ${Spacing.xxl} !important;

      html[dir="rtl"] & {
        padding-inline-end: ${Spacing.s} !important;
        padding-inline-start: ${Spacing.xxl} !important;
      }
    `
      : ''}
`;

const StyledInputWrapper = styled(Flex)`
  gap: 0.2rem;
  align-items: center;
  border-radius: ${Rounded.md};
`;

const StyledLabel = styled.label`
  cursor: pointer;
  color: ${({ theme }) => theme.fgPrimary};
  width: fit-content;
  flex: 1;
`;

const OptionalSpan = styled.span`
  color: ${({ theme }) => theme.fgMuted};
  display: inline-block;
  margin-inline-start: ${Spacing.s};
`;

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  title?: string;
  id?: string;
  valid?: boolean;
  width?: string;
  height?: string;
  border?: string;
  borderRadius?: keyof typeof Rounded;
  placeholder?: string;
  padding?: string;
  margin?: string;
  icon?: IconProp;
  errorMessage?: string;
  color?: string;
  titleAlign?: keyof typeof TitleAlignment;
  labelDirection?: keyof typeof LabelDirection;
  iconSize?: SizeProp;
  iconColor?: keyof ThemeType;
  dataTestId?: string;
  info?: string;
}

enum PasswordInputTypes {
  TEXT = 'TEXT',
  PASSWORD = 'PASSWORD',
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (props, ref) => {
    const { type, dataTestId, ...restProps } = props;

    const [inputType, setInputType] = useState(type);

    const { theme } = useTheme();

    const togglePasswordInputType = () => {
      setInputType(
        inputType === PasswordInputTypes.PASSWORD.toLowerCase()
          ? PasswordInputTypes.TEXT.toLowerCase()
          : PasswordInputTypes.PASSWORD.toLowerCase(),
      );
    };

    return (
      <Flex flexDirection="column" gap="s" width={props.width || '100%'}>
        <Flex
          width={'100%'}
          gap="s"
          flexDirection={
            props.labelDirection === LabelDirection.HORIZONTAL
              ? 'row'
              : 'column'
          }
        >
          {props.title ? (
            <StyledLabel
              htmlFor={props.id}
              style={{
                textAlign: props.titleAlign,
                alignSelf: props.titleAlign || 'flex-start',
                margin:
                  props.labelDirection === LabelDirection.HORIZONTAL
                    ? 'auto'
                    : '0',
              }}
            >
              {props.info ? (
                <Tooltip title={props.info}>
                  <AtiraIcon icon={faInfoCircle} color="fgPrimary" />
                  {/* don't delete this span */}
                  <span style={{ marginInlineEnd: '0.2rem' }}></span>
                </Tooltip>
              ) : null}
              {props.title}

              {props.required ? (
                <span style={{ color: theme.danger }}> *</span>
              ) : (
                <OptionalSpan>({t('common.optional')})</OptionalSpan>
              )}
            </StyledLabel>
          ) : null}

          {props.icon ? (
            <Flex flexDirection="column" position="relative" gap="s" flex={2}>
              <StyledInputWrapper
                opacity={props.disabled ? '0.2' : '1'}
                border={
                  props.valid === false
                    ? `1px solid ${theme.danger}`
                    : `1px solid ${theme.inputBorder}`
                }
                borderRadius={props.borderRadius || 'md'}
                style={{ padding: `0 ${Spacing.s}`, position: 'relative' }}
              >
                <StyledInput
                  ref={ref}
                  type={inputType}
                  style={props.style}
                  placeholder={props.placeholder}
                  data-testid={dataTestId}
                  {...restProps}
                  dir="auto"
                />

                <AtiraIcon
                  icon={props.icon}
                  size={props.iconSize || 'lg'}
                  color={props.iconColor || 'fgPrimary'}
                />
              </StyledInputWrapper>

              {props.errorMessage && props.valid === false ? (
                <Text
                  color="danger"
                  fontSize="s"
                  style={{ marginTop: Spacing.s }}
                >
                  {props.errorMessage}
                </Text>
              ) : null}
            </Flex>
          ) : (
            <Flex flexDirection="column" gap="s" flex={2}>
              <StyledInputWrapper position="relative">
                <StyledInput
                  ref={ref}
                  type={inputType}
                  originalType={type}
                  style={merge(
                    {},
                    props.style,
                    props.disabled ? { opacity: '0.2' } : {},
                    props.valid === false
                      ? { border: `1px solid ${theme.danger}` }
                      : { border: `1px solid ${theme.inputBorder}` },
                    {
                      padding: props.padding || `${Spacing.o} ${Spacing.s}`,
                    },
                  )}
                  placeholder={props.placeholder}
                  data-testid={dataTestId}
                  dir="auto"
                  {...restProps}
                />

                {type === PasswordInputTypes.PASSWORD.toLowerCase() ? (
                  <ShowHidePasswordButton
                    onClick={togglePasswordInputType}
                    hasError={
                      Boolean(props.errorMessage) && props.valid === false
                    }
                    icon={
                      inputType === PasswordInputTypes.PASSWORD.toLowerCase()
                        ? faEyeSlash
                        : faEye
                    }
                    type="button"
                    aria-label={
                      inputType === PasswordInputTypes.PASSWORD.toLowerCase()
                        ? t('showPassword')
                        : t('hidePassword')
                    }
                  />
                ) : null}
              </StyledInputWrapper>

              {props.errorMessage && props.valid === false ? (
                <Text
                  color="danger"
                  fontSize="s"
                  style={{ marginTop: Spacing.s }}
                >
                  {props.errorMessage}
                </Text>
              ) : null}
            </Flex>
          )}
        </Flex>
      </Flex>
    );
  },
);
